(function (root) {
  function options(variant) {
    return variant.options || [variant.option1, variant.option2, variant.option3].filter(value => value != null);
  }

  function matchVariant(variants, selections) {
    return variants.find(variant => options(variant).length === selections.length &&
      options(variant).every((value, index) => String(value) === String(selections[index])));
  }

  function optionAvailable(variants, selections, index, value) {
    const next = [...selections];
    next[index] = value;
    return matchVariant(variants, next)?.available === true;
  }

  function presentation(source, id) {
    const entry = source?.[String(id)];
    return entry?.verified === true ? entry : null;
  }

  function plan(item) {
    return item.selling_plan_allocation?.selling_plan?.id || null;
  }

  function properties(item) {
    return JSON.stringify(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  }

  function sameLine(item, original, variantId) {
    return String(item.variant_id) === String(variantId) &&
      String(plan(item)) === String(plan(original)) && properties(item) === properties(original);
  }

  function expectation(item, variantId, quantity) {
    return { variantId: String(variantId), properties: properties(item), planId: String(plan(item)), quantity };
  }

  function validVariantId(id) {
    return typeof id === 'number' ? Number.isSafeInteger(id) && id > 0 :
      typeof id === 'string' && /^[1-9]\d*$/.test(id);
  }

  function validCartItems(cart) {
    return Boolean(cart && Array.isArray(cart.items) && cart.items.every(item => item &&
      validVariantId(item.variant_id) && Number.isSafeInteger(item.quantity) && item.quantity > 0));
  }

  function variantQuantity(cart, variantId) {
    return cart.items.filter(item => String(item.variant_id) === variantId).reduce((sum, item) => sum + item.quantity, 0);
  }

  function withVariantTotals(before, mutation) {
    // Retained lines with omitted properties/plan must not masquerade as a removed identity.
    const variantTotals = mutation.checks.map(check => ({
      variantId: check.variantId,
      quantity: variantQuantity(before, check.variantId) + check.quantity -
        before.items.filter(item => matchesIdentity(item, check)).reduce((sum, item) => sum + item.quantity, 0),
    }));
    const result = { ...mutation, variantTotals };
    if (!validMutation(result)) throw mutationError(null);
    return result;
  }

  function validMutation(mutation) {
    return mutation && ['add', 'change', 'replace', 'restore'].includes(mutation.kind) &&
      Array.isArray(mutation.checks) && mutation.checks.length === (['replace', 'restore'].includes(mutation.kind) ? 2 : 1) &&
      mutation.checks.every(check => check && typeof check.variantId === 'string' && validVariantId(check.variantId) &&
        typeof check.properties === 'string' && typeof check.planId === 'string' &&
        Number.isSafeInteger(check.quantity) && check.quantity >= 0) &&
      new Set(mutation.checks.map(check => check.variantId)).size === mutation.checks.length &&
      Array.isArray(mutation.variantTotals) && mutation.variantTotals.length === mutation.checks.length &&
      mutation.checks.every(check => mutation.variantTotals.filter(total => total && total.variantId === check.variantId &&
        Number.isSafeInteger(total.quantity) && total.quantity >= check.quantity).length === 1) &&
      (mutation.kind !== 'replace' || (['add', 'remove'].includes(mutation.phase) &&
        Number.isSafeInteger(mutation.originalQuantity) && mutation.originalQuantity > 0 &&
        mutation.checks[0].quantity === 0 && mutation.checks[1].quantity >= mutation.originalQuantity &&
        mutation.checks[0].variantId !== mutation.checks[1].variantId &&
        mutation.checks[0].properties === mutation.checks[1].properties && mutation.checks[0].planId === mutation.checks[1].planId));
  }

  function matchesIdentity(item, check) {
    return String(item.variant_id) === check.variantId && properties(item) === check.properties && String(plan(item)) === check.planId;
  }

  function mutationMatches(cart, mutation) {
    return validCartItems(cart) && validMutation(mutation) &&
      mutation.variantTotals.every(total => variantQuantity(cart, total.variantId) === total.quantity) &&
      mutation.checks.every(check => cart.items.filter(item => matchesIdentity(item, check))
      .reduce((sum, item) => sum + item.quantity, 0) === check.quantity);
  }

  function replacementReady(cart, mutation) {
    if (!validCartItems(cart) || !validMutation(mutation) || mutation.kind !== 'replace' || mutation.phase !== 'add') return false;
    const originals = cart.items.filter(item => matchesIdentity(item, mutation.checks[0]));
    const targets = cart.items.filter(item => matchesIdentity(item, mutation.checks[1]));
    return originals.length === 1 && originals[0].quantity === mutation.originalQuantity &&
      targets.length === 1 && targets[0].quantity === mutation.checks[1].quantity &&
      mutation.variantTotals.every(total => variantQuantity(cart, total.variantId) === total.quantity +
        (total.variantId === mutation.checks[0].variantId ? mutation.originalQuantity : 0));
  }

  function quantityMutation(cart, lineOrKey, quantity) {
    if (!validCartItems(cart)) throw mutationError(null);
    const original = typeof lineOrKey === 'string' ? cart.items.find(item => item.key === lineOrKey) : cart.items[lineOrKey - 1];
    if (!original || !Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Your cart changed. Please review it.');
    const total = cart.items.filter(item => sameLine(item, original, original.variant_id)).reduce((sum, item) => sum + item.quantity, 0);
    return withVariantTotals(cart, { kind: 'change', checks: [expectation(original, original.variant_id, total - original.quantity + quantity)] });
  }

  function mutationError(cart, cause, pending = true) {
    const error = new Error(`${cause?.message || 'The requested cart update could not be confirmed.'} Review the current quantities before checking out.`);
    error.cart = cart;
    error.cartPending = pending;
    return error;
  }

  async function readAfterWrite(api) {
    try {
      return await readCart(api);
    } catch (_) {
      throw mutationError(null);
    }
  }

  async function readCart(api) {
    const cart = await api.read();
    if (!validCartItems(cart)) throw mutationError(null);
    return cart;
  }

  async function addVariant(api, variantId, quantity, itemProperties = {}, sellingPlanId = null) {
    const original = { properties: itemProperties, selling_plan_allocation: { selling_plan: { id: sellingPlanId } } };
    const total = cart => cart.items.filter(item => sameLine(item, original, variantId))
      .reduce((sum, item) => sum + item.quantity, 0);
    const before = await readCart(api);
    const baseQuantity = total(before);
    const mutation = withVariantTotals(before, { kind: 'add', checks: [expectation(original, variantId, baseQuantity + quantity)] });
    api.track?.(mutation);
    let addError;
    try {
      await api.add(variantId, quantity, itemProperties, sellingPlanId);
    } catch (error) {
      addError = error;
    }
    // An add is not idempotent. Reconcile its exact line identity instead of retrying the POST.
    const current = await readAfterWrite(api);
    // A terminal 422 may have added a partial quantity. Show the actual cart and error, never retry.
    if (addError?.cartTerminal) throw mutationError(current, addError, false);
    if (mutationMatches(current, mutation)) return current;
    throw mutationError(current, addError);
  }

  async function replaceVariant(api, lineKey, variantId) {
    const before = await readCart(api);
    const original = before.items.find(item => item.key === lineKey);
    if (!original) throw new Error('Your cart changed. Please select the item again.');
    if (String(original.variant_id) === String(variantId)) return before;
    const targetBefore = before.items.filter(item => sameLine(item, original, variantId));
    if (targetBefore.length > 1 || before.items.filter(item => sameLine(item, original, original.variant_id)).length !== 1) {
      throw new Error('This item has multiple price allocations. Please edit it in your cart.');
    }
    const baseQuantity = targetBefore[0]?.quantity || 0;
    const expectedQuantity = baseQuantity + original.quantity;
    const mutation = withVariantTotals(before, { kind: 'replace', phase: 'add', originalQuantity: original.quantity, checks: [
      expectation(original, original.variant_id, 0), expectation(original, variantId, expectedQuantity),
    ] });
    api.track?.(mutation);

    let addError;
    try {
      await api.add(variantId, original.quantity, original.properties || {}, plan(original));
    } catch (error) {
      addError = error;
    }
    // Read after every write, including lost responses; never retry an uncertain addition.
    const current = await readAfterWrite(api);
    if (addError?.cartTerminal) throw mutationError(current, addError, false);
    if (!replacementReady(current, mutation)) throw mutationError(current, addError);
    return removeReplacement(api, mutation, current);
  }

  async function finishReplacement(api, mutation) {
    if (!validMutation(mutation) || mutation.kind !== 'replace' || mutation.phase !== 'add') throw mutationError(null);
    const current = await readCart(api);
    if (mutationMatches(current, mutation)) return current;
    if (!replacementReady(current, mutation)) throw mutationError(current);
    // Explicit recovery removes only the original; it never repeats the add or sends a compensation.
    return removeReplacement(api, mutation, current, false);
  }

  async function removeReplacement(api, mutation, current, compensate = true) {
    const original = current.items.find(item => matchesIdentity(item, mutation.checks[0]));
    const variantId = mutation.checks[1].variantId;
    const expectedQuantity = mutation.checks[1].quantity;
    const baseQuantity = expectedQuantity - mutation.originalQuantity;
    api.track?.({ ...mutation, phase: 'remove' });
    let removeError;
    try {
      await api.change(original.key, 0);
    } catch (error) {
      // The removal may have succeeded on the server despite a network error.
      removeError = error;
    }
    current = await readAfterWrite(api);
    const remaining = current.items.filter(item => sameLine(item, original, original.variant_id));
    const targets = current.items.filter(item => sameLine(item, original, variantId));
    if (!remaining.length && targets.length === 1 && mutationMatches(current, mutation)) return current;

    // An unchanged GET can precede a delayed POST commit. Only a terminal response permits compensation.
    if (compensate && removeError?.cartTerminal && replacementReady(current, { ...mutation, phase: 'add' })) {
      const restore = withVariantTotals(current, { kind: 'restore', checks: [
        expectation(original, original.variant_id, original.quantity), expectation(original, variantId, baseQuantity),
      ] });
      api.track?.(restore);
      let rollbackError;
      try {
        await api.change(targets[0].key, baseQuantity);
      } catch (error) {
        // Preserve the original item and show the server's actual cart on failure.
        rollbackError = error;
      }
      current = await readAfterWrite(api);
      throw mutationError(current, removeError, !rollbackError?.cartTerminal && !mutationMatches(current, restore));
    }
    throw mutationError(current, removeError, !removeError?.cartTerminal);
  }

  const model = { matchVariant, optionAvailable, presentation, addVariant, replaceVariant, validCartItems, validMutation, mutationMatches, quantityMutation, replacementReady, finishReplacement };
  if (typeof module !== 'undefined' && module.exports) module.exports = model;
  else root.WolfVariantModel = model;
})(globalThis);
